import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TABS = [
  { k: "snapshot", label: "CEO Snapshot" },
  { k: "cases", label: "Cases" },
  { k: "finance", label: "Finance" },
  { k: "expenses", label: "Expenses" },
  { k: "investigations", label: "Investigations" },
  { k: "team", label: "Team" },
  { k: "bd", label: "BD" },
  { k: "timeseries", label: "Time-Series" },
];

export default function Analytics() {
  const [tab, setTab] = useState("snapshot");
  return (
    <div className="space-y-5" data-testid="analytics-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Analytics · Phase 5 / Track I</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Operational Dashboards</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Six functional dashboards + the CEO executive snapshot.</div>
        </div>
        <button
          onClick={async () => {
            const { data } = await api.get(`/analytics/ceo-snapshot.xlsx`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            const a = document.createElement("a"); a.href = url; a.download = "ceo_snapshot.xlsx";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }}
          className="span-btn span-btn-ghost"
          data-testid="analytics-export-xlsx"
        >
          Export CEO Snapshot · XLSX
        </button>
      </div>

      <div className="border-b border-[var(--span-border)] flex gap-6 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.k}
            data-testid={`analytics-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`py-3 text-[12px] uppercase font-medium tracking-[0.16em] border-b-2 whitespace-nowrap ${tab === t.k ? "border-black" : "border-transparent text-[var(--span-text-2)]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "snapshot" && <SnapshotPanel />}
      {tab === "cases" && <CasesPanel />}
      {tab === "finance" && <FinancePanel />}
      {tab === "expenses" && <ExpensesPanel />}
      {tab === "investigations" && <InvestigationsPanel />}
      {tab === "team" && <TeamPanel />}
      {tab === "bd" && <BDPanel />}
      {tab === "timeseries" && <TimeseriesPanel />}
    </div>
  );
}

// ---------- Reusable atoms ----------
function Stat({ label, value, sub, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display font-bold mt-1 tnum text-[28px] leading-none">{value}</div>
      {sub && <div className="text-[11px] text-[var(--span-text-2)] mt-2 font-mono">{sub}</div>}
    </div>
  );
}

function KVTable({ title, rows, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="label-eyebrow mb-3">{title}</div>
      <table className="span-table">
        <tbody>
          {(rows || []).map(([k, v]) => (
            <tr key={k}>
              <td className="font-mono text-[11px]">{k}</td>
              <td className="text-right tnum font-mono">{v}</td>
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr><td className="text-center py-4 text-[12px] text-[var(--span-text-2)]" colSpan={2}>No data.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Loading() { return <div className="font-mono text-xs text-[var(--span-text-2)] py-6">LOADING…</div>; }
function ErrorMsg({ msg }) { return <div className="font-mono text-xs text-[var(--span-danger)] py-6">{msg}</div>; }

function usePanel(path) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(path);
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load");
      }
    })();
  }, [path]);
  return { data, err };
}

const toRows = (obj) => Object.entries(obj || {}).map(([k, v]) => [k, v]);

// ---------- Panels ----------
function SnapshotPanel() {
  const { data, err } = usePanel("/analytics/ceo-snapshot");
  if (err) return <ErrorMsg msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4" data-testid="snapshot-panel">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Cases · open" value={data.cases?.open ?? "—"} sub={`Total ${data.cases?.total} · new 30d ${data.cases?.new_30d}`} testid="snap-cases" />
        <Stat label="Sightings · total" value={data.sightings?.total ?? "—"} sub={`Promoted ${data.sightings?.promoted}`} testid="snap-sightings" />
        <Stat label="Active employees" value={data.team?.active ?? "—"} sub={`${data.team?.mds} MD · ${data.team?.rms} RM`} testid="snap-team" />
        <Stat label="Revenue INR" value={data.finance ? fmtMoney(data.finance.revenue_inr, "INR") : "—"} sub={data.finance ? `${data.finance.invoices} invoices` : "Finance role only"} testid="snap-finance" />
      </div>
      <div className="text-[11px] text-[var(--span-text-2)] font-mono">As of {data.as_of}</div>
    </div>
  );
}

function CasesPanel() {
  const { data, err } = usePanel("/analytics/cases?days=90");
  const { data: dist } = usePanel("/analytics/case-type-distribution?months=12");
  if (err) return <ErrorMsg msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Total" value={data.total_cases} testid="cases-total" />
        <Stat label="Open" value={data.open_cases} testid="cases-open" />
        <Stat label="Closed" value={data.closed_cases} testid="cases-closed" />
        <Stat label="New (30d)" value={data.new_30d} testid="cases-new30" />
      </div>
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3">New cases · last 30 days</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data.trend_30d || []} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid stroke="#E5E5EA" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(d) => (d || "").slice(5)} />
            <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
            <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono', borderRadius: 2 }} />
            <Area type="monotone" dataKey="count" stroke="#0A0B0D" strokeWidth={1.5} fill="rgba(10,11,13,0.12)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {dist && <CaseTypeDistribution dist={dist} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KVTable title="By status" rows={toRows(data.by_status)} testid="cases-status" />
        <KVTable title="By priority" rows={toRows(data.by_priority)} testid="cases-priority" />
        <KVTable title="By country" rows={toRows(data.by_country)} testid="cases-country" />
        <KVTable title="By code" rows={toRows(data.by_code)} testid="cases-code" />
      </div>
    </div>
  );
}

const TYPE_COLORS = {
  Counterfeit:  "#0A0B0D",
  Diversion:    "#004EEB",
  Tampering:    "#F79009",
  Infringement: "#D92D20",
  Other:        "#98A2B3",
};

function CaseTypeDistribution({ dist }) {
  const totals = dist.totals_by_type || {};
  const pct = dist.totals_pct || {};
  return (
    <div className="card-flat p-5" data-testid="case-type-distribution">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-eyebrow">Case-Type Distribution</div>
          <div className="text-[11px] text-[var(--span-text-2)] mt-1 font-mono">
            New cases per month · last {dist.months_window} months · {dist.grand_total} total
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono">
          {dist.types.map(t => (
            <div key={t} className="flex items-center gap-1.5" data-testid={`legend-${t}`}>
              <span className="inline-block w-2.5 h-2.5" style={{ background: TYPE_COLORS[t] }} />
              <span className="text-[var(--span-text-2)] uppercase tracking-[0.12em]">{t}</span>
            </div>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={dist.series} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="#E5E5EA" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(m) => (m || "").slice(2)} />
          <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
          <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono', borderRadius: 2, border: '1px solid #E5E5EA' }} />
          {dist.types.map(t => (
            <Bar key={t} dataKey={t} stackId="ct" fill={TYPE_COLORS[t]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-[var(--span-border)]">
        {dist.types.map(t => (
          <div key={t} className="text-center" data-testid={`type-total-${t}`}>
            <div className="label-eyebrow text-[9px]" style={{ color: TYPE_COLORS[t] }}>{t}</div>
            <div className="font-display font-bold tnum text-[18px] mt-0.5">{totals[t] || 0}</div>
            <div className="text-[10px] text-[var(--span-text-2)] font-mono">{pct[t] || 0}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinancePanel() {
  const { data, err } = usePanel("/analytics/finance");
  if (err) return <ErrorMsg msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label={`Revenue (${data.year})`} value={fmtMoney(data.revenue_inr, "INR")} testid="fin-revenue" />
        <Stat label="GST 18% collected" value={fmtMoney(data.gst_18_collected_inr, "INR")} testid="fin-gst" />
        <Stat label="TDS 194J (est.)" value={fmtMoney(data.tds_194j_estimated_inr, "INR")} testid="fin-tds" />
        <Stat label="Total invoices" value={data.total_invoices} testid="fin-invoices" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KVTable title="Invoices by status" rows={toRows(data.invoices_by_status)} testid="fin-by-status" />
        <KVTable title="Clients by cadence" rows={toRows(data.clients_by_cadence)} testid="fin-by-cadence" />
      </div>
    </div>
  );
}

function ExpensesPanel() {
  const { data, err } = usePanel("/analytics/expenses");
  if (err) return <ErrorMsg msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="BD spend · INR" value={fmtMoney(data.bd_totals_by_currency?.INR || 0, "INR")} testid="exp-bd-inr" />
        <Stat label="Claims · total INR" value={fmtMoney(data.claims_total_inr || 0, "INR")} testid="exp-claims" />
        <Stat label="Currencies" value={Object.keys(data.bd_totals_by_currency || {}).length} testid="exp-currencies" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KVTable title="BD totals by currency" rows={toRows(data.bd_totals_by_currency)} testid="exp-by-currency" />
        <KVTable title="BD by category (INR)" rows={toRows(data.bd_by_category_inr)} testid="exp-by-category" />
        <KVTable title="Claims by tier" rows={toRows(data.claims_by_tier)} testid="exp-by-tier" />
      </div>
    </div>
  );
}

function InvestigationsPanel() {
  const { data, err } = usePanel("/analytics/investigations");
  if (err) return <ErrorMsg msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Sightings · total" value={data.total_sightings} testid="inv-total" />
        <Stat label="Promoted to case" value={data.promoted_to_case} testid="inv-promoted" />
        <Stat label="Evidence items" value={data.evidence_items} testid="inv-evidence" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KVTable title="Sightings by status" rows={toRows(data.by_status)} testid="inv-by-status" />
        <KVTable title="By recommendation" rows={toRows(data.by_recommendation)} testid="inv-by-rec" />
      </div>
    </div>
  );
}

function TeamPanel() {
  const { data, err } = usePanel("/analytics/team");
  if (err) return <ErrorMsg msg={err} />;
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label="Active employees" value={data.active_employees} testid="team-active" />
        <Stat label="Managing Directors" value={data.mds} testid="team-mds" />
        <Stat label="Reporting Managers" value={data.rms} testid="team-rms" />
        <Stat label="Leave · pending" value={data.leave_pending} testid="team-leave-pending" />
      </div>
      <KVTable title="By department" rows={toRows(data.by_department)} testid="team-by-dept" />
    </div>
  );
}

function BDPanel() {
  const [period, setPeriod] = useState("");
  const { data, err } = usePanel(`/analytics/bd${period ? `?period=${period}` : ""}`);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="label-eyebrow">Period</label>
        <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-Q1" className="span-input font-mono h-9 w-[160px]" />
      </div>
      {err && <ErrorMsg msg={err} />}
      {!data && !err && <Loading />}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Budget rows" value={data.budget_count} testid="bd-rows" />
          <Stat label="Budget total" value={fmtMoney(data.budget_total_inr || 0, "INR")} testid="bd-budget" />
          <Stat label="Spend · 90 days" value={fmtMoney(data.spend_90d_inr || 0, "INR")} testid="bd-spend" />
        </div>
      )}
    </div>
  );
}

function TimeseriesPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get(`/analytics/timeseries?days=${days}`); setData(data); })(); }, [days]);
  if (!data) return <Loading />;

  const totals = data.series.reduce((acc, d) => {
    acc.cases += d.cases; acc.revenue += d.revenue_inr; acc.esc += d.escalations;
    return acc;
  }, { cases: 0, revenue: 0, esc: 0 });
  const fmtAxis = (n) => n >= 100000 ? `${(n / 100000).toFixed(0)}L` : n >= 1000 ? `${(n / 1000).toFixed(0)}k` : n;

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {[7, 14, 30, 60, 90].map(d => (
          <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)} className={`h-9 px-3 text-[11px] font-mono uppercase tracking-[0.16em] border ${days === d ? "bg-black text-white border-black" : "bg-white text-[var(--span-text-2)] border-[var(--span-border)]"}`}>
            {d}d
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Cases (period)" value={totals.cases} sub={`${(totals.cases / days).toFixed(1)} / day avg`} />
        <Stat label="Revenue billed (INR)" value={fmtMoney(totals.revenue, "INR")} sub="Sum of client invoices created" />
        <Stat label="Escalations triggered" value={totals.esc} sub={`${days} day window`} />
      </div>
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3">Cases created</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data.series} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0A0B0D" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#0A0B0D" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#E5E5EA" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
            <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono', borderRadius: 2, border: '1px solid #E5E5EA' }} />
            <Area type="monotone" dataKey="cases" stroke="#0A0B0D" strokeWidth={1.5} fill="url(#g1)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-5">
          <div className="label-eyebrow mb-3">Revenue billed (INR)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.series} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#E5E5EA" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono', borderRadius: 2, border: '1px solid #E5E5EA' }} formatter={(v) => fmtMoney(v, "INR")} />
              <Bar dataKey="revenue_inr" fill="#004EEB" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-flat p-5">
          <div className="label-eyebrow mb-3">Escalations triggered</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.series} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#E5E5EA" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono', borderRadius: 2, border: '1px solid #E5E5EA' }} />
              <Line type="monotone" dataKey="escalations" stroke="#D92D20" strokeWidth={1.5} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
